import React, { useState, useEffect } from 'react';
import MainUrl from '../../MainUrl';
import {
    X,
    Calendar,
    Clock,
    User,
    MapPin,
    Phone,
    MessageSquare,
    CheckCircle,
    UtensilsCrossed,
    Leaf,
    Drumstick,
    Flame,
    AlertCircle,
    MinusCircle,
    PlusCircle,
    ShoppingBag
} from 'lucide-react';

const BookingModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        address: '',
        date: '',
        time: '12:00 - 1:00 PM', // Hardcoded delivery time
        mealPlan: '', // Changed from servings to mealPlan
        menuType: '', // regular or special
        message: '',
        selectedItems: [],
        // New fields for multi-day selection
        selectedDays: [],
    });

    const [formStatus, setFormStatus] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [disabledOptions, setDisabledOptions] = useState({
        tomorrow: false,
        threeDays: false,
        fullWeek: false,
        monthly: false
    });

    // Meal plan options - Added Monthly option
    const mealPlanOptions = [
        { value: 'tomorrow', label: 'Single Day' },
        { value: 'threeDays', label: 'Three Days' },
        { value: 'fullWeek', label: 'Full Week (Monday - Friday)' },
        { value: 'monthly', label: 'Monthly (20 Working Days)' }
    ];

    // Check which meal plans should be disabled based on current day
    useEffect(() => {
        updateDisabledMealPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.date]);

    // Change: Updated to handle weekday-only selection and monthly option
    const updateDisabledMealPlans = () => {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        let disabled = {
            tomorrow: false,
            threeDays: false,
            fullWeek: false,
            monthly: false
        };

        if (dayOfWeek === 1) { // Monday
            disabled.fullWeek = true;
        } else if (dayOfWeek === 3) { // Wednesday
            disabled.fullWeek = true;
            disabled.threeDays = true;
        } else if (dayOfWeek >= 5 || dayOfWeek === 0) {
            // Friday, Saturday, Sunday — all options are allowed for next week
            disabled = {
                tomorrow: false,
                threeDays: false,
                fullWeek: false,
                monthly: false
            };
        }

        // Reset invalid selection
        if (
            (disabled.threeDays && formData.mealPlan === 'threeDays') ||
            (disabled.fullWeek && formData.mealPlan === 'fullWeek')
        ) {
            setFormData(prev => ({ ...prev, mealPlan: '' }));
        }

        setDisabledOptions(disabled);
    };

    // Daily Menu - All items available every day
    const dailyMenuItems = [
        {
            id: 'aloo-matar',
            name: 'Aloo Matar / Kartoffeln mit Erbsen',
            description: 'Potatoes and green peas in a spiced tomato gravy / Kartoffeln und grüne Erbsen in würziger Tomatensoße',
            price: 7.95,
            isVeg: true,
            isSpicy: false,
            isCombo: true // Part of default combo
        },
        {
            id: 'zeera-rice',
            name: 'Zeera Rice / Kreuzkümmel-Reis',
            description: 'Basmati rice tempered with cumin seeds / Basmati-Reis mit Kreuzkümmel',
            price: 3.95,
            isVeg: true,
            isSpicy: false,
            isCombo: true // Part of default combo
        },
        {
            id: 'yellow-daal',
            name: 'Yellow Daal / Gelbes Linsen-Dal',
            description: 'Yellow lentils tempered with cumin and garlic / Gelbe Linsen mit Kreuzkümmel und Knoblauch',
            price: 6.95,
            isVeg: true,
            isSpicy: false,
            isCombo: true // Part of default combo
        },
        {
            id: 'mixed-veg',
            name: 'Mixed Veg / Gemischtes Gemüse',
            description: 'Assorted vegetables in a mildly spiced curry / Verschiedene Gemüsesorten in mild gewürztem Curry',
            price: 7.95,
            isVeg: true,
            isSpicy: false,
            isCombo: true // Part of default combo
        },
        {
            id: 'matar-paneer',
            name: 'Matar Paneer / Erbsen mit Paneer',
            description: 'Cottage cheese and green peas in a creamy tomato sauce / Paneer und grüne Erbsen in cremiger Tomatensoße',
            price: 8.95,
            isVeg: true,
            isSpicy: false,
            isCombo: true // Part of default combo
        },
        {
            id: 'chapati',
            name: 'Chapati / Chapati (Fladenbrot)',
            description: 'Traditional Indian flatbread / Traditionelles indisches Fladenbrot',
            price: 1.50,
            isVeg: true,
            isSpicy: false,
            isCombo: false // Extra item
        },
        {
            id: 'puri',
            name: 'Puri / Puri (Frittiertes Brot)',
            description: 'Deep-fried, puffed Indian bread / Frittiertes, aufgeblähtes indisches Brot',
            price: 2.50,
            isVeg: true,
            isSpicy: false,
            isCombo: false // Extra item
        },
        {
            id: 'papad',
            name: 'Papad / Papadam',
            description: 'Crispy lentil wafer / Knuspriger Linsen-Fladen',
            price: 1.50,
            isVeg: true,
            isSpicy: false,
            isCombo: false // Extra item
        },
        {
            id: 'salad',
            name: 'Salad / Salat',
            description: 'Fresh mixed salad with Indian dressing / Frischer gemischter Salat mit indischem Dressing',
            price: 3.95,
            isVeg: true,
            isSpicy: false,
            isCombo: false // Extra item
        }
    ];

    // Special Menu - Available for special orders (Saturday & Sunday only)
    const specialMenuItems = [
        {
            id: 'chilli-chicken',
            name: 'Chilli Chicken / Chili-Hähnchen',
            description: 'Spicy chicken stir-fry with bell peppers and onions / Scharfes Hähnchen-Pfannengericht mit Paprika und Zwiebeln',
            price: 10.95,
            isVeg: false,
            isSpicy: true
        },
        {
            id: 'fried-rice',
            name: 'Fried Rice / Gebratener Reis',
            description: 'Indo-Chinese style fried rice with vegetables / Indo-chinesischer gebratener Reis mit Gemüse',
            price: 6.95,
            isVeg: true,
            isSpicy: false
        },
        {
            id: 'chicken-chowmein',
            name: 'Chicken Chowmein / Hähnchen-Nudeln',
            description: 'Stir-fried noodles with chicken and vegetables / Gebratene Nudeln mit Hähnchen und Gemüse',
            price: 9.95,
            isVeg: false,
            isSpicy: false
        },
        {
            id: 'veg-momo',
            name: 'Veg Momo / Gemüse-Momos',
            description: 'Steamed Nepali dumplings filled with spiced vegetables / Gedämpfte nepalesische Teigtaschen gefüllt mit gewürztem Gemüse',
            price: 8.95,
            isVeg: true,
            isSpicy: true
        },
        {
            id: 'chicken-steamed-momo',
            name: 'Chicken Steamed Momo / Gedämpfte Hähnchen-Momos',
            description: 'Steamed Nepali dumplings filled with spiced chicken / Gedämpfte nepalesische Teigtaschen gefüllt mit gewürztem Hähnchen',
            price: 9.95,
            isVeg: false,
            isSpicy: true
        },
        {
            id: 'samosa',
            name: 'Samosa / Samosa',
            description: 'Crispy fried pastry filled with spiced potatoes and peas / Knusprige frittierte Teigtaschen gefüllt mit gewürzten Kartoffeln und Erbsen',
            price: 3.95,
            isVeg: true,
            isSpicy: false
        },
        {
            id: 'chicken-curry',
            name: 'Chicken Curry / Hähnchen-Curry',
            description: 'Tender chicken pieces in an aromatic curry sauce / Zartes Hähnchenfleisch in aromatischer Curry-Sauce',
            price: 9.95,
            isVeg: false,
            isSpicy: true
        },
        {
            id: 'chilli-paneer',
            name: 'Chilli Paneer / Chili-Paneer',
            description: 'Spicy cottage cheese stir-fry with bell peppers / Scharfes Paneer-Pfannengericht mit Paprika',
            price: 9.95,
            isVeg: true,
            isSpicy: true
        }
    ];


    // Function to auto-add combo items for regular menu
    const autoAddComboItems = () => {
        const comboItems = dailyMenuItems.filter(item => item.isCombo);

        setFormData(prev => {
            // Get items that are already selected
            const existingItems = prev.selectedItems.filter(item =>
                comboItems.some(comboItem => comboItem.id === item.id)
            );

            // Add new combo items with quantity 1 if not already selected
            const newItems = comboItems.filter(item =>
                !prev.selectedItems.some(selectedItem => selectedItem.id === item.id)
            ).map(item => ({ ...item, quantity: 1 }));

            return {
                ...prev,
                selectedItems: [...existingItems, ...newItems]
            };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Make a copy of the current formData to work with
        const updatedFormData = { ...formData, [name]: value };

        // Set the updated form data
        setFormData(updatedFormData);

        // Handle side effects after form data is updated
        if (name === 'mealPlan') {
            updateSelectedDaysForMealPlan(updatedFormData.mealPlan);
        }
    };

    // Function to update selected days based on meal plan
    const updateSelectedDaysForMealPlan = (mealPlan) => {
        const today = new Date();
        let selectedDays = [];

        const getNextWeekday = (date) => {
            let d = new Date(date);
            d.setDate(d.getDate() + 1);
            while (d.getDay() === 0 || d.getDay() === 6) {
                d.setDate(d.getDate() + 1);
            }
            return d;
        };

        const getUpcomingMonday = () => {
            const date = new Date();
            const day = date.getDay();
            const diff = day === 0 ? 1 : 8 - day; // Next Monday
            date.setDate(date.getDate() + diff);
            return date;
        };

        const getMonthlyWeekdays = () => {
            const monday = getUpcomingMonday();
            const weekdays = [];
            let currentDate = new Date(monday);

            // Get 20 working days (4 weeks)
            for (let week = 0; week < 4; week++) {
                for (let day = 0; day < 5; day++) {
                    const workDay = new Date(currentDate);
                    workDay.setDate(currentDate.getDate() + (week * 7) + day);
                    weekdays.push(new Date(workDay));
                }
            }
            return weekdays;
        };

        switch (mealPlan) {
            case 'tomorrow':
                selectedDays = [getNextWeekday(today)];
                break;
            case 'threeDays':
                let day = getNextWeekday(today);
                selectedDays.push(new Date(day));
                for (let i = 0; i < 2; i++) {
                    day = getNextWeekday(day);
                    selectedDays.push(new Date(day));
                }
                break;
            case 'fullWeek':
                const monday = getUpcomingMonday();
                for (let i = 0; i < 5; i++) {
                    const day = new Date(monday);
                    day.setDate(monday.getDate() + i);
                    selectedDays.push(new Date(day));
                }
                break;
            case 'monthly':
                selectedDays = getMonthlyWeekdays();
                break;
            default:
                selectedDays = [];
        }

        setFormData(prev => ({
            ...prev,
            selectedDays
        }));

        // Auto-add combo items for regular menu
        if (selectedDays.length > 0 && formData.menuType === 'regular') {
            // Use setTimeout to ensure state update happens after selectedDays is set
            setTimeout(() => {
                autoAddComboItems();
            }, 0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('loading');

        try {
            // Prepare the order data according to the API structure
            const orderData = {
                // Customer details
                name: formData.name,
                phone: formData.phone,
                email: '', // You can add email field to the form if needed
                address: formData.address,
                preferred_language: 'en', // You can make this dynamic based on user selection

                // Order details
                delivery_date: formData.selectedDays[0]?.toISOString().split('T')[0], // First delivery date
                delivery_time: formData.time,
                number_of_servings: formData.selectedDays.length,
                special_instructions: formData.message,
                language: 'en',

                // Items
                items: formData.selectedItems.map(item => ({
                    item_id: item.id,
                    quantity: item.quantity
                }))
            };

            // Make API call
            const response = await fetch(`${MainUrl}/api/v1/orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData)
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                setFormStatus('success');

                // Reset form after successful submission
                setTimeout(() => {
                    setFormStatus(null);
                    setFormData({
                        name: '',
                        phone: '',
                        address: '',
                        date: '',
                        time: '12:00 - 1:00 PM',
                        mealPlan: '',
                        menuType: '',
                        message: '',
                        selectedItems: [],
                        selectedDays: [],
                    });
                    setCurrentStep(1);
                    onClose();
                }, 2000);
            } else {
                setFormStatus('error');
                console.error('Order creation failed:', result.message);

                // Reset error status after 3 seconds
                setTimeout(() => {
                    setFormStatus(null);
                }, 3000);
            }
        } catch (error) {
            setFormStatus('error');
            console.error('Error creating order:', error);

            // Reset error status after 3 seconds
            setTimeout(() => {
                setFormStatus(null);
            }, 3000);
        }
    };

    const nextStep = () => {
        setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    // Add or remove item from selection
    const toggleMenuItem = (item) => {
        const itemIndex = formData.selectedItems.findIndex(i => i.id === item.id);

        if (itemIndex === -1) {
            // Add item with quantity 1
            setFormData(prev => ({
                ...prev,
                selectedItems: [...prev.selectedItems, { ...item, quantity: 1 }]
            }));
        } else {
            // Remove item only if it's not a combo item for regular menu
            const isCombo = isComboItem(item.id);

            if (!isCombo) {
                setFormData(prev => ({
                    ...prev,
                    selectedItems: prev.selectedItems.filter(i => i.id !== item.id)
                }));
            }
        }
    };

    // Change quantity of selected item
    const updateItemQuantity = (itemId, change) => {
        setFormData(prev => {
            const updatedItems = prev.selectedItems.map(item => {
                if (item.id === itemId) {
                    const isCombo = isComboItem(itemId);

                    // For combo items, minimum quantity is 1
                    const minQuantity = isCombo ? 1 : 0;
                    const newQuantity = Math.max(minQuantity, item.quantity + change);

                    return { ...item, quantity: newQuantity };
                }
                return item;
            });

            // Remove items with 0 quantity (only non-combo items can reach 0)
            return {
                ...prev,
                selectedItems: updatedItems.filter(item => item.quantity > 0)
            };
        });
    };

    // Calculate total price based on meal plan
    const calculateTotal = () => {
        // Base price from items
        const itemsTotal = formData.selectedItems.reduce((total, item) => total + (item.price * item.quantity), 0);

        // Apply multiplier based on meal plan and selected days
        let multiplier = formData.selectedDays.length || 1;

        return itemsTotal * multiplier;
    };

    // Check if an item is selected
    const isItemSelected = (itemId) => {
        return formData.selectedItems.some(item => item.id === itemId);
    };

    // Get quantity of a selected item
    const getItemQuantity = (itemId) => {
        const item = formData.selectedItems.find(item => item.id === itemId);
        return item ? item.quantity : 0;
    };

    // Check if item is a combo item (cannot be removed for regular menu)
    const isComboItem = (itemId) => {
        if (formData.menuType !== 'regular') return false;
        const item = dailyMenuItems.find(item => item.id === itemId);
        return item?.isCombo || false;
    };

    // Get human-readable description of the meal plan
    const getMealPlanDescription = () => {
        switch (formData.mealPlan) {
            case 'tomorrow':
                return 'Single Day';
            case 'threeDays':
                return 'Three Days';
            case 'fullWeek':
                return 'Full Week (Monday - Friday)';
            case 'monthly':
                return 'Monthly (20 Working Days)';
            default:
                return '';
        }
    };

    // Format delivery dates based on meal plan selection
    const getDeliveryDatesDisplay = () => {
        if (formData.selectedDays.length === 0) return '';

        if (formData.selectedDays.length === 1) {
            // Single day format
            return formData.selectedDays[0].toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } else {
            // Multiple days format (range)
            const firstDay = formData.selectedDays[0];
            const lastDay = formData.selectedDays[formData.selectedDays.length - 1];

            return `${firstDay.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            })} - ${lastDay.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            })}`;
        }
    };

    // Food indicator component for veg/non-veg/spicy
    const FoodIndicator = ({ isVeg, isSpicy }) => (
        <div className="flex space-x-2">
            {isVeg ? (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded flex items-center">
                    <Leaf size={12} className="mr-1" /> Veg
                </span>
            ) : (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded flex items-center">
                    <Drumstick size={12} className="mr-1" /> Non-Veg
                </span>
            )}

            {isSpicy && (
                <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded flex items-center">
                    <Flame size={12} className="mr-1" /> Spicy
                </span>
            )}
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl relative overflow-hidden flex flex-col max-h-[90vh]">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
                    aria-label="Close booking modal"
                >
                    <X size={24} />
                </button>

                {/* Header */}
                <div className="p-6 text-white" style={{ backgroundColor: '#7bae37' }}>
                    <h2 className="text-2xl font-bold">Order Tiffin Service</h2>
                    <p className="mt-1 opacity-90">Fresh, homemade Indian meals delivered to you</p>
                </div>

                {formStatus === 'success' ? (
                    /* Success Message */
                    <div className="p-8 text-center">
                        <div className="flex justify-center mb-4">
                            <CheckCircle size={64} className="text-green-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Order Confirmed!</h3>
                        <p className="text-gray-600">
                            Thank you for your tiffin order. We've received your order and will contact you soon for confirmation.
                        </p>
                    </div>
                ) : formStatus === 'error' ? (
                    /* Error Message */
                    <div className="p-8 text-center">
                        <div className="flex justify-center mb-4">
                            <AlertCircle size={64} className="text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Order Failed!</h3>
                        <p className="text-gray-600">
                            Sorry, there was an error processing your order. Please try again or contact us directly.
                        </p>
                        <button
                            onClick={() => setFormStatus(null)}
                            className="mt-4 px-6 py-2 text-white rounded-md font-medium transition-colors"
                            style={{ backgroundColor: '#7bae37' }}
                        >
                            Try Again
                        </button>
                    </div>
                ) : formStatus === 'loading' ? (
                    /* Loading Message */
                    <div className="p-8 text-center">
                        <div className="flex justify-center mb-4">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: '#7bae37' }}></div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Processing Order...</h3>
                        <p className="text-gray-600">
                            Please wait while we process your order.
                        </p>
                    </div>
                ) : (
                    /* Booking Form */
                    <form onSubmit={handleSubmit}>
                        <div className="p-6 max-h-[calc(100vh-250px)] overflow-y-auto">
                            {/* Step indicators */}
                            <div className="flex mb-6">
                                <div className={`flex-1 h-1 ${currentStep >= 1 ? 'bg-green-500' : 'bg-gray-200'}`} style={currentStep >= 1 ? { backgroundColor: '#7bae37' } : {}}></div>
                                <div className={`flex-1 h-1 ${currentStep >= 2 ? 'bg-green-500' : 'bg-gray-200'}`} style={currentStep >= 2 ? { backgroundColor: '#7bae37' } : {}}></div>
                                <div className={`flex-1 h-1 ${currentStep >= 3 ? 'bg-green-500' : 'bg-gray-200'}`} style={currentStep >= 3 ? { backgroundColor: '#7bae37' } : {}}></div>
                            </div>

                            {currentStep === 1 ? (
                                /* Step 1: Menu Type and Meal Plan */
                                <div className="space-y-4">
                                    <h3 className="font-medium text-gray-800 mb-4">Select your delivery details</h3>

                                    {/* Hardcoded Delivery Time Display */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Clock size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Delivery Time
                                        </label>
                                        <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                                            {formData.time}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            We deliver during lunch hours
                                        </p>
                                    </div>

                                    {/* Menu Type Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <UtensilsCrossed size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Menu Type
                                        </label>
                                        <select
                                            name="menuType"
                                            value={formData.menuType}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                        >
                                            <option value="">Select menu type</option>
                                            <option value="regular">Regular Menu (Daily Combo)</option>
                                            <option value="special">Special Menu (Saturday & Sunday Only)</option>
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Special menu is only available on weekends
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Calendar size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Meal Plan
                                        </label>
                                        <select
                                            name="mealPlan"
                                            value={formData.mealPlan}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                        >
                                            <option value="">Select a meal plan</option>
                                            {mealPlanOptions.map((option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                    disabled={disabledOptions[option.value]}
                                                >
                                                    {option.label} {disabledOptions[option.value] ? '(Not Available)' : ''}
                                                    {formData.selectedDays.length > 0 && option.value === formData.mealPlan ?
                                                        ` (${getDeliveryDatesDisplay()})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Note: Some options may be unavailable based on the current day
                                        </p>
                                    </div>
                                </div>
                            ) : currentStep === 2 ? (
                                /* Step 2: Menu Selection based on menuType */
                                <div className="space-y-4">
                                    {formData.menuType === 'regular' ? (
                                        /* Regular Menu - Show combo items and extras */
                                        <div>
                                            <div className="flex items-center text-white px-4 py-3 rounded shadow mb-4" style={{ backgroundColor: '#7bae37' }}>
                                                <Calendar className="mr-2" size={18} />
                                                <h3 className="text-sm font-semibold">Regular Menu - Daily Combo</h3>
                                                <p className="opacity-90 text-sm ml-auto truncate hidden sm:block">
                                                    Complete meal combo
                                                </p>
                                            </div>

                                            <div className="max-h-96 overflow-y-auto">
                                                {/* Combo Items Section */}
                                                <div className="mb-6">
                                                    <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                                                        <CheckCircle size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                                        Default Combo (Included)
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {dailyMenuItems.filter(item => item.isCombo).map((item) => {
                                                            const isSelected = isItemSelected(item.id);

                                                            return (
                                                                <div key={item.id} className="p-4 border-2 rounded-lg hover:shadow-md transition-all bg-green-50" style={{ borderColor: '#7bae37' }}>
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-1">
                                                                            <span className="font-medium text-gray-800">{item.name}</span>

                                                                            {item.description && (
                                                                                <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                                                            )}

                                                                            <div className="mt-2 flex items-center">
                                                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded flex items-center inline-flex">
                                                                                    <Leaf size={12} className="mr-1" /> Vegetarian
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        <div className="text-right flex flex-col space-y-2 items-end">
                                                                            <span className="text-xs text-gray-500">Included</span>

                                                                            {isSelected && (
                                                                                <div className="inline-flex items-center border border-gray-300 rounded-md">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-1 text-gray-300 cursor-not-allowed"
                                                                                        disabled={true}
                                                                                    >
                                                                                        <MinusCircle size={14} />
                                                                                    </button>
                                                                                    <span className="px-2 text-sm min-w-6 text-center">
                                                                                        {getItemQuantity(item.id)}
                                                                                    </span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-1 text-gray-500 hover:text-green-600"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            updateItemQuantity(item.id, 1);
                                                                                        }}
                                                                                    >
                                                                                        <PlusCircle size={14} />
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Extra Items Section */}
                                                <div>
                                                    <h4 className="font-medium text-gray-800 mb-3 flex items-center">
                                                        <PlusCircle size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                                        Add Extra Items
                                                    </h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {dailyMenuItems.filter(item => !item.isCombo).map((item) => {
                                                            const isSelected = isItemSelected(item.id);

                                                            return (
                                                                <div key={item.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all bg-white">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-1">
                                                                            <span className="font-medium text-gray-800">{item.name}</span>

                                                                            {item.description && (
                                                                                <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                                                            )}

                                                                            <div className="mt-2">
                                                                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded flex items-center inline-flex">
                                                                                    <Leaf size={12} className="mr-1" /> Vegetarian
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        <div className="text-right flex flex-col space-y-2 items-end ml-2">
                                                                            <span className="font-bold" style={{ color: '#7bae37' }}>€{item.price.toFixed(2)}</span>

                                                                            {isSelected ? (
                                                                                <div className="inline-flex items-center border border-gray-300 rounded-md">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-1 text-gray-500 hover:text-green-600"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            updateItemQuantity(item.id, -1);
                                                                                        }}
                                                                                    >
                                                                                        <MinusCircle size={14} />
                                                                                    </button>
                                                                                    <span className="px-2 text-sm min-w-6 text-center">
                                                                                        {getItemQuantity(item.id)}
                                                                                    </span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="p-1 text-gray-500 hover:text-green-600"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            updateItemQuantity(item.id, 1);
                                                                                        }}
                                                                                    >
                                                                                        <PlusCircle size={14} />
                                                                                    </button>
                                                                                </div>
                                                                            ) : (
                                                                                <button
                                                                                    type="button"
                                                                                    className="p-1"
                                                                                    style={{ color: '#7bae37' }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        toggleMenuItem(item);
                                                                                    }}
                                                                                >
                                                                                    <PlusCircle size={20} />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Special Menu - Available on Saturday & Sunday */
                                        <div>
                                            <div className="flex items-center text-white px-4 py-3 rounded shadow mb-4" style={{ backgroundColor: '#7bae37' }}>
                                                <Drumstick className="mr-2" size={18} />
                                                <h3 className="text-sm font-semibold">Special Menu</h3>
                                                <p className="opacity-90 text-sm ml-auto truncate hidden sm:block">
                                                    Weekend specials
                                                </p>
                                            </div>

                                            <div className="max-h-96 overflow-y-auto">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {specialMenuItems.map((item) => {
                                                        const isSelected = isItemSelected(item.id);

                                                        return (
                                                            <div key={item.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all bg-white">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <span className="font-medium text-gray-800">{item.name}</span>

                                                                        {item.description && (
                                                                            <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                                                        )}

                                                                        <div className="mt-2">
                                                                            <FoodIndicator isVeg={item.isVeg} isSpicy={item.isSpicy} />
                                                                        </div>
                                                                    </div>

                                                                    <div className="text-right flex flex-col space-y-2 items-end ml-2">
                                                                        <span className="font-bold" style={{ color: '#7bae37' }}>€{item.price.toFixed(2)}</span>

                                                                        {isSelected ? (
                                                                            <div className="inline-flex items-center border border-gray-300 rounded-md">
                                                                                <button
                                                                                    type="button"
                                                                                    className="p-1 text-gray-500 hover:text-green-600"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        updateItemQuantity(item.id, -1);
                                                                                    }}
                                                                                >
                                                                                    <MinusCircle size={14} />
                                                                                </button>
                                                                                <span className="px-2 text-sm min-w-6 text-center">
                                                                                    {getItemQuantity(item.id)}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="p-1 text-gray-500 hover:text-green-600"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        updateItemQuantity(item.id, 1);
                                                                                    }}
                                                                                >
                                                                                    <PlusCircle size={14} />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="p-1"
                                                                                style={{ color: '#7bae37' }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleMenuItem(item);
                                                                                }}
                                                                            >
                                                                                <PlusCircle size={20} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <p className="text-sm text-gray-500 mt-4">
                                                    Special menu items are available on weekends only
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Selected Items Summary */}
                                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="flex items-center mb-3">
                                            <ShoppingBag size={18} className="mr-2" style={{ color: '#7bae37' }} />
                                            <h4 className="font-medium">Your Selection</h4>
                                        </div>

                                        {formData.selectedItems.length === 0 ? (
                                            <p className="text-gray-500 text-sm">No items selected yet</p>
                                        ) : (
                                            <>
                                                <div className="max-h-40 overflow-y-auto pr-1">
                                                    <table className="w-full text-sm">
                                                        <tbody>
                                                            {formData.selectedItems.map((item) => (
                                                                <tr key={item.id} className="align-top">
                                                                    <td className="py-1 pr-1 w-full text-right">
                                                                        {item.name} x{item.quantity}
                                                                        {isComboItem(item.id) && (
                                                                            <span className="text-green-600 text-xs ml-1">(Combo)</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="py-1 text-right whitespace-nowrap">
                                                                        €{(item.price * item.quantity).toFixed(2)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="border-t border-gray-200 mt-2 pt-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span>Single day price:</span>
                                                        <span>€{(formData.selectedItems.reduce((total, item) => total + (item.price * item.quantity), 0)).toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span>Delivery days:</span>
                                                        <span>{formData.selectedDays.length} day{formData.selectedDays.length !== 1 ? 's' : ''}</span>
                                                    </div>
                                                    <div className="flex justify-between font-medium pt-1 mt-1 border-t border-gray-200">
                                                        <span>Total:</span>
                                                        <span>€{calculateTotal().toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* Step 3: Contact Information */
                                <div className="space-y-4">
                                    <h3 className="font-medium text-gray-800 mb-4">Your contact information</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <User size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                            placeholder="Your name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <Phone size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                            placeholder="Your phone number"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <MapPin size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Delivery Address
                                        </label>
                                        <textarea
                                            name="address"
                                            value={formData.address || ''}
                                            onChange={handleChange}
                                            required
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                            placeholder="Your complete delivery address"
                                        ></textarea>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Please provide your full address including street, building number, apartment, city, and postal code
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                            <MessageSquare size={16} className="mr-2" style={{ color: '#7bae37' }} />
                                            Delivery Instructions (Optional)
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            rows="3"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                                            style={{ '&:focus': { ringColor: '#7bae37', borderColor: 'transparent' } }}
                                            placeholder="Any special instructions for delivery?"
                                        ></textarea>
                                    </div>

                                    {/* Order Summary in Step 3 */}
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <h4 className="font-medium mb-2">Order Summary</h4>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span>Delivery Days:</span>
                                                <span>{formData.selectedDays.length} ({getMealPlanDescription()})</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Delivery Time:</span>
                                                <span>{formData.time}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Items:</span>
                                                <span>{formData.selectedItems.length}</span>
                                            </div>
                                            <div className="mt-1 pt-1 border-t border-gray-200">
                                                {formData.selectedItems.map((item) => (
                                                    <div key={item.id} className="flex justify-between text-xs py-1">
                                                        <span>
                                                            {item.name} x{item.quantity}
                                                            {isComboItem(item.id) && (
                                                                <span className="text-green-600 ml-1">(Combo)</span>
                                                            )}
                                                        </span>
                                                        <span>€{(item.price * item.quantity).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex justify-between text-sm pt-1 mt-1 border-t border-gray-200">
                                                <span>Single day price:</span>
                                                <span>€{(formData.selectedItems.reduce((total, item) => total + (item.price * item.quantity), 0)).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between font-medium pt-1 mt-1 border-t border-gray-200">
                                                <span>Total:</span>
                                                <span>€{calculateTotal().toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Form Buttons */}
                        <div className="px-6 py-4 bg-gray-50 flex justify-between sticky bottom-0 border-t border-gray-200">
                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        disabled={!formData.mealPlan || !formData.menuType}
                                        className="px-6 py-2 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#7bae37' }}
                                    >
                                        Next: Select Menu
                                    </button>
                                </>
                            ) : currentStep === 2 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={prevStep}
                                        className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        disabled={formData.selectedItems.length === 0}
                                        className="px-6 py-2 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#7bae37' }}
                                    >
                                        Next: Contact Info
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={prevStep}
                                        className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={formStatus === 'loading'}
                                        className="px-6 py-2 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#7bae37' }}
                                    >
                                        {formStatus === 'loading' ? 'Processing...' : 'Confirm Order'}
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BookingModal;